import { calculateInvitationCounts, calculateTicketCounts } from '@features/modal-invitation/lib/calculation-utils';
import { TicketDeliveryMethod } from '@pages/event/api/useSendMessages';
import type { SelectValue } from '@shared/ui/select';
import {
  attachSmsCostToSmsRow,
  previewInvitationChannels,
  previewTicketChannels,
  type PreviewChannelItem,
} from './preview-channels';
import { INVITATION_TYPE_BOTH, INVITATION_TYPE_EMAIL, type InvitationTypeValue } from './invitation-types';
import { mapChannelsToLegacyType } from './invitation-channel-options';

export const SMS_UNIT_COST_RUB = 5;

const toDeliveryMethod = (typeValue: InvitationTypeValue): TicketDeliveryMethod => {
  switch (typeValue) {
    case INVITATION_TYPE_EMAIL:
      return TicketDeliveryMethod.Email;
    case INVITATION_TYPE_BOTH:
      return TicketDeliveryMethod.Both;
    default:
      return TicketDeliveryMethod.SMS;
  }
};

export const formatSmsCostLabel = (totalSmsMessages: number): string | null => {
  if (totalSmsMessages <= 0) return null;
  const rub = totalSmsMessages * SMS_UNIT_COST_RUB;
  return `~ ${rub.toLocaleString('ru-RU')} ₽`;
};

export const buildInvitationDeliveryPreviewChannels = (
  selectedChannels: SelectValue[],
  emailCount: number,
  smsCount: number,
  vkCount: number,
  isOutlookMeetingEnabled: boolean,
  invitationTextParts: number
): PreviewChannelItem[] => {
  const legacyTypeValue = mapChannelsToLegacyType(selectedChannels);
  const smsTotal = legacyTypeValue
    ? calculateInvitationCounts(toDeliveryMethod(legacyTypeValue), smsCount, invitationTextParts).sms
    : 0;

  const channels = previewInvitationChannels({
    selectedChannels,
    emailCount,
    smsCount: smsTotal,
    vkCount,
    includeOutlook: isOutlookMeetingEnabled,
  });

  return attachSmsCostToSmsRow(channels, formatSmsCostLabel(smsTotal));
};

export const buildTicketDeliveryPreviewChannels = (
  selectedTicketChannels: SelectValue[],
  emailCount: number,
  smsCount: number,
  vkCount: number,
  isTicketSettingsCollapsed: boolean,
  ticketTextParts: number
): PreviewChannelItem[] => {
  if (isTicketSettingsCollapsed || !selectedTicketChannels.length) {
    return [];
  }

  const legacyTypeValue = mapChannelsToLegacyType(selectedTicketChannels);
  const smsTotal = legacyTypeValue
    ? calculateTicketCounts(toDeliveryMethod(legacyTypeValue), smsCount, ticketTextParts).sms
    : 0;

  const channels = previewTicketChannels({
    selectedChannels: selectedTicketChannels,
    emailCount,
    smsCount: smsTotal,
    vkCount,
  });

  if (!channels.length) {
    return channels;
  }

  return attachSmsCostToSmsRow(channels, formatSmsCostLabel(smsTotal));
};
